import { useState } from "react";
import { Loader2, Mail, X } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { batchSyncEmails } from "../../services/api";
import { getGraphToken } from "../../services/msal";
import { inputStyle } from "./constants";

export default function EmailSyncSection() {
  const [mailboxes, setMailboxes] = useState([""]);
  const [sinceDate, setSinceDate] = useState(
    new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  );
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);

  const addMailbox = () => setMailboxes((prev) => [...prev, ""]);
  const removeMailbox = (i) => setMailboxes((prev) => prev.filter((_, idx) => idx !== i));
  const updateMailbox = (i, val) =>
    setMailboxes((prev) => prev.map((m, idx) => (idx === i ? val : m)));

  const handleSync = async () => {
    const validMailboxes = mailboxes.filter((m) => m.trim() && m.includes("@"));
    if (!validMailboxes.length) {
      setError("Enter at least one valid email address.");
      return;
    }
    setRunning(true);
    setResult(null);
    setError(null);
    setProgress(null);
    try {
      let offset = 0;
      const batchSize = 25;
      let totals = { contactsProcessed: 0, totalContacts: 0, totalMessages: 0, synced: 0, updated: 0, errors: 0 };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const graphToken = await getGraphToken();
        const data = await batchSyncEmails(graphToken, {
          mailboxes: validMailboxes,
          sinceDate,
          limit: batchSize,
          offset,
        });
        totals.contactsProcessed += data.contactsProcessed;
        totals.totalContacts = data.totalContacts;
        totals.totalMessages += data.totalMessages;
        totals.synced += data.synced;
        totals.updated += data.updated;
        totals.errors += data.errors;
        if (data.sampleErrors?.length && !totals.sampleErrors) totals.sampleErrors = data.sampleErrors;
        setProgress({ processed: totals.contactsProcessed, total: data.totalContacts });

        if (!data.hasMore) break;
        offset = data.nextOffset;
      }

      setResult(totals);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  return (
    <div style={{ background: colors.surface, borderRadius: 12, padding: 24, border: `1px solid ${colors.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <Mail size={18} color={colors.accent} />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.text }}>Email History Sync</h3>
      </div>
      <p style={{ fontSize: 12, color: colors.textSecondary, margin: "0 0 16px 0" }}>
        Pull email history from O365 mailboxes for all CRM contacts. You need delegate access to sync other users' mailboxes.
        This may take several minutes for large contact lists.
      </p>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: colors.text, display: "block", marginBottom: 6 }}>
          Mailboxes to sync
        </label>
        {mailboxes.map((m, i) => (
          <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <input
              value={m}
              onChange={(e) => updateMailbox(i, e.target.value)}
              placeholder="user@domain.com"
              style={inputStyle}
            />
            {mailboxes.length > 1 && (
              <button
                onClick={() => removeMailbox(i)}
                style={{
                  background: "none",
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  cursor: "pointer",
                  padding: "0 8px",
                  color: colors.textSecondary,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={addMailbox}
          style={{
            background: "none",
            border: "none",
            color: colors.accent,
            fontSize: 12,
            cursor: "pointer",
            padding: 0,
            fontFamily: fonts.body,
          }}
        >
          + Add another mailbox
        </button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: colors.text, display: "block", marginBottom: 6 }}>
          Sync emails since
        </label>
        <input
          type="date"
          value={sinceDate}
          onChange={(e) => setSinceDate(e.target.value)}
          style={{ ...inputStyle, width: 200 }}
        />
      </div>

      <button
        onClick={handleSync}
        disabled={running}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 20px",
          borderRadius: 8,
          border: "none",
          background: running ? colors.border : colors.accent,
          color: "#fff",
          fontSize: 13,
          fontWeight: 600,
          cursor: running ? "default" : "pointer",
          fontFamily: fonts.body,
        }}
      >
        {running ? (
          <>
            <Loader2 size={14} className="spin" />
            Syncing...
          </>
        ) : (
          <>
            <Mail size={14} />
            Start Batch Sync
          </>
        )}
      </button>

      {progress && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>
            <span>Processing contacts...</span>
            <span>{progress.processed} / {progress.total}</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: colors.border, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              borderRadius: 3,
              background: colors.accent,
              width: `${Math.round((progress.processed / progress.total) * 100)}%`,
              transition: "width 0.3s ease",
            }} />
          </div>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: "rgba(239,68,68,0.1)", color: "#ef4444", fontSize: 12 }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 6, background: "rgba(16,185,129,0.08)", border: `1px solid rgba(16,185,129,0.2)` }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#10b981", marginBottom: 8 }}>Sync Complete</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", fontSize: 12, color: colors.text }}>
            <span style={{ color: colors.textSecondary }}>Contacts processed:</span>
            <span style={{ fontWeight: 600 }}>{result.contactsProcessed} / {result.totalContacts}</span>
            <span style={{ color: colors.textSecondary }}>Messages found:</span>
            <span style={{ fontWeight: 600 }}>{result.totalMessages}</span>
            <span style={{ color: colors.textSecondary }}>New emails cached:</span>
            <span style={{ fontWeight: 600 }}>{result.synced}</span>
            <span style={{ color: colors.textSecondary }}>Existing updated:</span>
            <span style={{ fontWeight: 600 }}>{result.updated}</span>
            {result.errors > 0 && (
              <>
                <span style={{ color: "#ef4444" }}>Errors:</span>
                <span style={{ fontWeight: 600, color: "#ef4444" }}>{result.errors}</span>
              </>
            )}
          </div>
          {result.sampleErrors?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: "#ef4444", fontFamily: "monospace" }}>
              {result.sampleErrors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
